import React, { useEffect, useMemo, useRef, useState } from 'react'
import AppInput from '../../atoms/AppInput/AppInput'
import FilterPanel from '../FilterPanel/FilterPanel'
import { useOperationModes } from 'core/hooks/useOperationModes'
// Filtro de plantilla deshabilitado (ya no aplica). Mantener comentado por si
// se re-habilita en el futuro.
// import { getTemplatesRulesGroup } from 'services/templateService'
import { SearchFilterValues } from 'interfaces/searchFilter'
import './styles.css'
import { DynamicIcon } from 'components/atoms/Icons'

interface Props {
    onChange: (data: SearchFilterValues) => void
    initialValue?: SearchFilterValues
    counts?: {
        all: number
        inProcess: number
        finished: number
        canceled: number
    }
}

const EMPTY_FILTER: SearchFilterValues = {
    search: null,
    states: [],
    date: { start: null, end: null },
    classificationTypeId: null,
    classificationTypeCode: '',
    withoutContainerization: null,
}

const STATE_CHIPS = [
    { value: 'IN_PROCESS', label: 'En proceso' },
    { value: 'FINISHED', label: 'Finalizado' },
    { value: 'CANCELED', label: 'Cancelado' },
] as const

// "Activado" (con contenerización) -> withoutContainerization=false
// "Desactivado" (sin contenerización) -> withoutContainerization=true
const containerizationToFlag = (choice: string): boolean | null =>
    choice === 'on' ? false : choice === 'off' ? true : null

const SearchFilter = ({ onChange, initialValue }: Props) => {
    const { operationModes } = useOperationModes()
    const [filter, setFilter] = useState<SearchFilterValues>(initialValue ?? EMPTY_FILTER)
    const [panelOpen, setPanelOpen] = useState(false)
    const panelRef = useRef<HTMLDivElement>(null)

    // --- Filtro de plantilla (deshabilitado; mantener para posible re-habilitación) ---
    // const [templates, setTemplates] = useState<{ value: string; label: string }[]>([])
    // useEffect(() => {
    //     let active = true
    //     getTemplatesRulesGroup()
    //         // eslint-disable-next-line @typescript-eslint/no-explicit-any
    //         .then((res: { data: any[] }) => {
    //             if (active) setTemplates(res.data.map((t) => ({ value: String(t.id), label: t.name })))
    //         })
    //         .catch(() => { if (active) setTemplates([]) })
    //     return () => { active = false }
    // }, [])

    // Cierra el panel al hacer click fuera.
    // Usa composedPath() en vez de contains(target): el target puede
    // desmontarse en el mismo mousedown (p. ej. al elegir una opción del
    // select interno), y entonces contains daría false y cerraría el panel
    // por error. composedPath conserva la cadena de nodos del evento.
    useEffect(() => {
        if (!panelOpen) return
        const handleClickOutside = (e: MouseEvent) => {
            const panel = panelRef.current
            if (panel && !e.composedPath().includes(panel)) {
                setPanelOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [panelOpen])

    // Emite el nuevo filtro de forma directa (sin useEffect emisor).
    const emit = (next: SearchFilterValues) => {
        setFilter(next)
        onChange(next)
    }

    const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            emit({ ...filter, search: (e.target as HTMLInputElement).value })
        }
    }

    const handleState = (value: string) => {
        const states = filter.states.includes(value)
            ? filter.states.filter((s) => s !== value)
            : [...filter.states, value]
        emit({ ...filter, states })
    }

    const handleApplyPanel = (values: Record<string, unknown>) => {
        const code = (values['classificationTypeCode'] as string) ?? ''
        const mode = operationModes.find((m) => m.code === code)
        // Filtro de plantilla deshabilitado:
        // const templateId = (values['templateId'] as string) ?? ''
        // const templateMeta = values['templateId__meta'] as Record<string, unknown> | undefined

        emit({
            ...filter,
            classificationTypeId: mode?.id ?? null,
            classificationTypeCode: code,
            // templateId: templateId ? Number(templateId) : null,
            // templateName: (templateMeta?.name as string) ?? '',
            withoutContainerization: containerizationToFlag((values['containerization'] as string) ?? ''),
        })
        setPanelOpen(false)
    }

    const handleResetPanel = () => {
        emit({
            ...filter,
            classificationTypeId: null,
            classificationTypeCode: '',
            // templateId: null,
            // templateName: '',
            withoutContainerization: null,
        })
        setPanelOpen(false)
    }

    const activeFilterCount = [
        filter.classificationTypeCode,
        // filter.templateName,
        filter.withoutContainerization != null ? 'c' : '',
    ].filter(Boolean).length

    const panelInitialValues = useMemo(
        () => ({
            classificationTypeCode: filter.classificationTypeCode ?? '',
            // templateId: filter.templateId != null ? String(filter.templateId) : '',
            containerization:
                filter.withoutContainerization == null
                    ? ''
                    : filter.withoutContainerization ? 'off' : 'on',
        }),
        [filter.classificationTypeCode, filter.withoutContainerization]
    )

    return (
        <div className="flex items-center gap-4">
            {/* Izquierda: buscador + botón filtro */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-[229px]">
                    <AppInput
                        placeholder="Buscar..."
                        name="search"
                        defaultValue={initialValue?.search ?? ''}
                        onKeyDown={handleSearch}
                    />
                </div>

                <div className="relative" ref={panelRef}>
                    <button
                        type="button"
                        onClick={() => setPanelOpen((o) => !o)}
                        className={`relative flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${
                            activeFilterCount > 0
                                ? 'border-green-500 bg-green-50'
                                : 'border-[#CBD5E1] bg-white hover:bg-gray-50'
                        }`}
                        aria-label="Abrir filtros"
                        aria-expanded={panelOpen}
                    >
                        <DynamicIcon
                            name="filter"
                            className={`w-5 h-5 ${activeFilterCount > 0 ? 'text-green-600' : 'text-gray-500'}`}
                        />
                        {activeFilterCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 rounded-full bg-green-500 text-white text-[10px] font-bold leading-none">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>

                    {panelOpen && (
                        <div className="absolute left-0 top-12 z-50 w-80">
                            <FilterPanel
                                onClose={() => setPanelOpen(false)}
                                onApply={handleApplyPanel}
                                onReset={handleResetPanel}
                                initialValues={panelInitialValues}
                            >
                                <FilterPanel.RadioGroup
                                    name="classificationTypeCode"
                                    label="Tipo de orden"
                                    options={operationModes.map((m) => ({
                                        value: m.code,
                                        label: m.name,
                                    }))}
                                />
                                {/* Filtro de plantilla deshabilitado (ya no aplica).
                                    Mantener comentado por si se re-habilita.
                                <FilterPanel.SearchableSelect
                                    name="templateId"
                                    label="Plantilla"
                                    options={templates}
                                /> */}
                                <FilterPanel.RadioGroup
                                    name="containerization"
                                    label="Contenerización"
                                    options={[
                                        { value: 'on', label: 'Activado' },
                                        { value: 'off', label: 'Desactivado' },
                                    ]}
                                />
                            </FilterPanel>
                        </div>
                    )}
                </div>
            </div>

            {/* Derecha: chips de estado */}
            <div className="flex gap-2 shrink-0">
                {STATE_CHIPS.map((chip) => (
                    <button
                        key={chip.value}
                        onClick={() => handleState(chip.value)}
                        aria-pressed={filter.states.includes(chip.value)}
                        className={`h-9 px-4 py-2 rounded-full text-sm font-normal leading-[18px] border transition-colors ${
                            filter.states.includes(chip.value)
                                ? 'bg-gray-800 text-white border-gray-800'
                                : 'bg-white text-[#3E5060] border-[#CBD5E1] hover:bg-gray-50'
                        }`}
                    >
                        {chip.label}
                    </button>
                ))}
            </div>
        </div>
    )
}

export default SearchFilter
